import { useState, type FormEvent } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../lib/i18n';
import { contactThreadPath, useContactBook } from '../../../lib/contact-book';
import { outreachCard, outreachContext, type OutreachContextEntry } from '../../../lib/island-quickview';
import { askKind, type OpenAsksRead, type WebOpenAsk } from '../../../lib/open-asks';
import { dismissAsk, isClosableAsk, isReplyableAsk, markAskAnswered, type AskCloseCache } from '../../../lib/ask-close';
import { queryKeys } from '../../../lib/query-client';
import { useOpenConversationInPlace, useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { messageDir } from '../../../lib/message-direction';
import { QuickViewRowButton } from '../AgentsIslandQuickViewRow';
import { Button, Card, Input, TextButton, Toast } from '../ui';
import { useCategoryRowClass } from '../category-view/CategoryView';
import { TaskDecisionPrompts } from './TaskDecisionPrompts';
import { postClientSignal } from './ChaseAskGroup';
import type { ActionNeededCard as Item } from './tasks-list-logic';
import styles from './tasks.module.css';

/**
 * ONE card of the Action needed tab that is not a task row (`actionNeededCards`), slotted among the rows by
 * recency:
 *  - an OUTREACH-ONLY question — the island's own outreach card (person and channel, the thread, the question with
 *    its chips, "Open conversation" to the thread);
 *  - an OPEN ASK (`lib/open-asks.ts`) — a Decision answers in place through the SAME `TaskDecisionPrompts` block
 *    every question card wears; a ledger ask shows what the push said. Both end in "Open conversation", to the
 *    chat the ask was put in.
 * Answering re-reads `/api/tasks` (the block's `['tasks']` invalidation), which drops the card on every screen.
 */
export function ActionNeededCard({ item }: { item: Item }) {
  const rowSurface = useCategoryRowClass();
  const key = item.kind === 'outreach' ? item.question.decisionId : item.ask.id;
  return (
    <div className={`${styles.cardRow}${rowSurface ? ` ${styles.cardRowInList}` : ''}`} data-tasks-action-needed={key}>
      <Card className={`${styles.card}${rowSurface ? ` ${rowSurface}` : ''}`}>
        {item.kind === 'outreach' ? <OutreachQuestion item={item} /> : <OpenAsk ask={item.ask} />}
      </Card>
    </div>
  );
}

function OutreachQuestion({ item }: { item: Extract<Item, { kind: 'outreach' }> }) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const { logPageClickBi } = useGenericBi();
  const contactBook = useContactBook('');
  const contacts = (contactBook.kind === 'ready' ? contactBook.contacts : []) as OutreachContextEntry[];
  const outreach = outreachContext(item.question, contacts, t('contacts.row.unnamed'), i18n.language);
  return (
    // An outreach-only card has no row button, so `onOpen` is never reached; "Open conversation" is the way in.
    <QuickViewRowButton
      task={outreachCard(item.question)}
      onOpen={() => undefined}
      outreach={outreach}
      page={TASKS_PAGE}
      onOpenConversation={(ctx) => {
        logPageClickBi({ ...TASKS_PAGE, button_name: 'open_conversation_from_question' }, { additionalFields: { outreach_id: ctx.outreachId } });
        void router.push(contactThreadPath(ctx.identity, ctx.outreachId));
      }}
    />
  );
}

function OpenAsk({ ask }: { ask: WebOpenAsk }) {
  const { t } = useTranslation();
  const maestro = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const openInPlace = useOpenConversationInPlace();
  // Only a signal to re-render after an answer lands; the row re-reads from the cache.
  const [, setAnsweredHere] = useState(false);
  const fallback = t('outreach.line.needsYouFallback', { maestroAgentName: maestro.name });
  // Opens the chat BESIDE the list, never `/?conversation=…`: that route is Home, so the reader lost the
  // Tasks screen they were working through.
  const open = () => {
    logPageClickBi({ ...TASKS_PAGE, button_name: 'open_conversation_from_ask' }, { additionalFields: { ask_kind: askKind(ask) } });
    openInPlace(ask.conversationId);
  };
  const link = (
    <TextButton size="small" underline onClick={open}>
      {t('outreach.island.openConversation')}
    </TextButton>
  );
  if (ask.prompt) {
    const prompt = ask.prompt.question?.trim() ? ask.prompt : { ...ask.prompt, question: fallback };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* No task behind it (`id: ''`): the verdict carries none and the BI no `task_id`. A goal question's
            `goalId` is what the answer flow writes the verdict against. */}
        <TaskDecisionPrompts
          task={{ id: '', goalId: ask.goalId }}
          prompts={[prompt]}
          origin={null}
          onAnsweredChange={setAnsweredHere}
          page={TASKS_PAGE}
          pillChips
          rowKey={ask.id}
        />
        <span style={{ display: 'flex' }}>{link}</span>
      </div>
    );
  }
  const title = ask.title || (ask.body ? undefined : fallback);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <button type="button" onClick={open} className={styles.askNotice}>
        {title ? (
          <span className={styles.askTitle} dir={messageDir(title)}>
            {title}
          </span>
        ) : null}
        {ask.body ? (
          <span className={styles.askBody} dir={messageDir(ask.body)}>
            {ask.body}
          </span>
        ) : null}
      </button>
      {isReplyableAsk(ask) ? <AskReply ask={ask} /> : null}
      <span className={styles.askActions}>
        {link}
        {isClosableAsk(ask) ? <NotForMe ask={ask} /> : null}
      </span>
    </div>
  );
}

/**
 * A reply typed on the row, sent into the ask's own conversation — the same message the chat would send, so the
 * agent picks it up as an answer and the ledger resolves the ask when it lands. The chat opens beside the list
 * with the reply in it, so the agent's answer is right there; the row leaves at once.
 */
function AskReply({ ask }: { ask: WebOpenAsk }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { logPageClickBi } = useGenericBi();
  const sendInChat = useOpenSideChat();
  const [draft, setDraft] = useState('');
  const send = (e: FormEvent) => {
    e.preventDefault();
    const message = draft.trim();
    if (!message) return;
    logPageClickBi({ ...TASKS_PAGE, button_name: 'reply_from_ask' }, { additionalFields: { ask_kind: askKind(ask) } });
    sendInChat({ kind: 'existing', conversationId: ask.conversationId, message });
    markAskAnswered(ask, askCloseCache(queryClient));
    setDraft('');
  };
  return (
    <form className={styles.askReply} onSubmit={send}>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={t('chat.reply.action')}
        aria-label={t('chat.reply.action')}
        dir="auto"
      />
      <Button type="submit" size="large" disabled={!draft.trim()}>
        {t('common.send')}
      </Button>
    </form>
  );
}

/** "Not for me": closes an ask the owner will not act on (ASK_DISMISSED). A refused close puts the row back. */
function NotForMe({ ask }: { ask: WebOpenAsk }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { logPageClickBi } = useGenericBi();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const dismiss = async () => {
    if (busy) return;
    setBusy(true);
    logPageClickBi({ ...TASKS_PAGE, button_name: 'ask_not_for_me' }, { additionalFields: { ask_kind: askKind(ask) } });
    const ok = await dismissAsk(ask, askCloseCache(queryClient), postClientSignal);
    if (!ok) {
      setBusy(false);
      setFailed(true);
    }
  };
  return (
    <>
      <TextButton size="small" onClick={() => void dismiss()} disabled={busy}>
        {t('tasks.ask.notForMe')}
      </TextButton>
      <Toast open={failed} message={t('tasks.chase.error')} onClose={() => setFailed(false)} />
    </>
  );
}

function askCloseCache(queryClient: QueryClient): AskCloseCache {
  return {
    updateOpenAsks: (update) => queryClient.setQueryData<OpenAsksRead | null | undefined>(queryKeys.openAsks, update),
    invalidateTasks: () => void queryClient.invalidateQueries({ queryKey: queryKeys.tasksAllScopes }),
  };
}
